import { useState, useMemo } from 'react'
import moment, { Moment } from 'moment'
import 'moment/locale/fr'
import {
  Box, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, MenuItem, Badge, Tooltip,
  Alert,
  Typography,
} from '@mui/material'
import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import { getFrenchHolidays } from '@utils/date.utils'
import { CreateProductOrderLineItemDto, ProductCatalogDto } from '@/services/dtos'

moment.locale('fr')

interface Props {
  generators: (Pick<CreateProductOrderLineItemDto, 'quantity' | 'plannedDates'> & { product: ProductCatalogDto })[]
  addPlannedDate: (date: string, productId: string, quantity: number) => void
  removePlannedDate: (date: string, productId: string) => void
}

function CustomDay(
  props: PickersDayProps<Moment> & {
    holidays: Record<string, string>
    generators: (Pick<CreateProductOrderLineItemDto, 'quantity' | 'plannedDates'> & { product: ProductCatalogDto })[]
  }
) {
  const { day, holidays, generators, ...other } = props
  const dateKey = day.format('YYYY-MM-DD')
  const holiday = holidays[dateKey]
  const allocated = generators.reduce((acc, { plannedDates }) => {
    const planned = (plannedDates ?? []).find(p => p.date === dateKey)
    return acc + (planned?.quantity ?? 0)
  }, 0)

  const dayButton = (
    <Badge
      overlap="circular"
      badgeContent={allocated || undefined}
      color="secondary"
    >
      <PickersDay
        {...other}
        day={day}
        sx={{
          ...(holiday && {
            color: 'warning.main',
            fontWeight: 'bold',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 2,
              width: 4,
              height: 4,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
            },
          }),
          ...(allocated > 0 && {
            backgroundColor: 'primary.light',
            color: 'primary.contrastText',
            '&:hover': { backgroundColor: 'primary.main' },
          }),
        }}
      />
    </Badge>
  )

  return holiday ? (
    <Tooltip title={holiday} arrow>
      {dayButton}
    </Tooltip>
  ) : dayButton
}

export function ProductOrderGeneratorCalendar({ generators, addPlannedDate, removePlannedDate }: Props) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [dialogProductId, setDialogProductId] = useState('')
  const [dialogQuantity, setDialogQuantity] = useState<number | null>(1)
  const [currentMonth, setCurrentMonth] = useState<Moment>(moment())
  const holidays = useMemo(
    () => getFrenchHolidays(currentMonth.year()),
    [currentMonth.year()]
  )

  const remainingByProduct = useMemo(() => {
    return Object.fromEntries(
      generators.map(({ product, quantity, plannedDates }) => [product.id, (quantity ?? 0) - 
        ((plannedDates ?? []).reduce((s, d) => s + (d.quantity ?? 0), 0) || 0)])
    )
  }, [generators])

  const handleDayClick = (date: Moment) => {
    const key = date.format('YYYY-MM-DD')
    setSelectedDate(key)
    const first = generators.find(g => remainingByProduct[g.product.id] > 0)
    setDialogProductId(first?.product.id || '')
    setDialogQuantity(1)
  }

  const handleConfirm = () => {
    if (!selectedDate || !dialogProductId) return
    addPlannedDate(dialogProductId, selectedDate, dialogQuantity ?? 0)
    setSelectedDate(null)
  }

  const handleRemove = (dateKey: string, productId: string) => {
    removePlannedDate(dateKey, productId)
  }

  const maxQuantity = dialogProductId ? (remainingByProduct[dialogProductId] || 0) : 0

  const getGeneratorsByDate = (dateKey: string) => 
    generators.filter(({ plannedDates }) => plannedDates?.some(({ date }) => date === dateKey))

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {generators.map(({ product, quantity }) => (
          <Chip
            key={product.id}
            label={`${product.name}: ${(quantity ?? 0) - remainingByProduct[product.id]}/${quantity}`}
            color={remainingByProduct[product.id] === 0 ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
        ))}
      </Box>

      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="fr">
        <DateCalendar
          disablePast
          onMonthChange={(month) => setCurrentMonth(month)}
          onChange={(date) => date && handleDayClick(date)}
          slots={{ day: CustomDay as any }}
          slotProps={{
            day: { holidays, generators } as any,
          }}
          sx={{
            mx: 0,
            width: '100%',
            maxWidth: 600,
            '& .MuiDayCalendar-weekContainer': {
              justifyContent: 'space-around',
            },
            '& .MuiDayCalendar-header': {
              justifyContent: 'space-around',
            },
            '& .MuiDayCalendar-monthContainer': {
              overflow: 'visible',
            },
            '& .MuiDayCalendar-slideTransition': {
              overflow: 'visible',
            },
        }}
        />
      </LocalizationProvider>

      <Dialog open={!!selectedDate} onClose={() => setSelectedDate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Planification - {selectedDate && moment(selectedDate).format('dddd D MMMM YYYY')}
          {selectedDate && holidays[selectedDate] && (
            <Chip label={holidays[selectedDate]} color="warning" size="small" sx={{ ml: 1 }} />
          )}
        </DialogTitle>

        <DialogContent>
          {getGeneratorsByDate(selectedDate!).length > 0 ? (
            <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {getGeneratorsByDate(selectedDate!).map(({ product, plannedDates }) => {
                const quantity = plannedDates?.find(p => p.date === selectedDate)?.quantity ?? 0
                return (
                  <Chip
                    key={`${selectedDate}-${product.id}`}
                    label={`${product.name} x${quantity}`}
                    onDelete={() => handleRemove(selectedDate!, product.id)}
                  />
                )
               })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucun générateur prévu pour cette date.
            </Typography>
          )}

          {generators.filter(({ product }) => remainingByProduct[product.id] > 0).length > 0 && (<div className="mt-2">
            <TextField
              label="Générateur"
              select
              fullWidth
              value={dialogProductId}
              onChange={(e) => { setDialogProductId(e.target.value); setDialogQuantity(1) }}
              sx={{ mt: 1, mb: 1 }}
            >
              {generators.filter(({ product }) => remainingByProduct[product.id] > 0).map(({ product }) => (
                <MenuItem key={product.id} value={product.id}>
                  {product.name}
                </MenuItem>
              ))}
            </TextField>
            {dialogProductId && <Alert severity="info">
              {remainingByProduct[dialogProductId]} date{remainingByProduct[dialogProductId] > 1 ? 's' : ''}{' '}
              restante{remainingByProduct[dialogProductId] > 1 ? 's' : ''} pour ce type de générateur.
            </Alert>}
            <TextField
              label="Quantité"
              fullWidth 
              type="number" 
              value={dialogQuantity}
              onChange={(e) => {
                  const value = e.target.value
                  setDialogQuantity(!value ? null : Math.max(1, Math.min(maxQuantity, parseInt(value) || 1)))
              }}
              inputProps={{ min: 1, max: maxQuantity }}
            />
          </div>)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedDate(null)}>Fermer</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={maxQuantity <= 0}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
